import type { TFunction } from "i18next";
import Modal from "../../../components/Modal";
import type { FeedbackFormState } from "../types/dashboard.types";

interface DashboardFeedbackModalProps {
  open: boolean;
  form: FeedbackFormState;
  loading: boolean;
  message: string | null;
  error: string | null;
  canSubmit: boolean;
  t: TFunction;
  onClose: () => void;
  onFieldChange: (field: "title" | "body", value: string) => void;
  onFilesChange: (files: FileList | null) => void;
  onSubmit: () => void;
}

const DashboardFeedbackModal = ({ open, form, loading, message, error, canSubmit, t, onClose, onFieldChange, onFilesChange, onSubmit }: DashboardFeedbackModalProps) => (
  <Modal show={open} title={t("dashboard.report_issue", { defaultValue: "Report an issue" })} type="info" onClose={onClose} hideDefaultActions>
    <div className="space-y-3 text-left">
      {message ? (
        <div className="space-y-3">
          <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800 dark:border-green-900 dark:bg-green-900/30 dark:text-green-100">
            {message}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200"
            >
              {t("common.actions.close", { defaultValue: "Close" })}
            </button>
          </div>
        </div>
      ) : (
        <>
          {error ? (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/30 dark:text-red-100">
              {error}
            </div>
          ) : null}
          <div>
            <label className="text-sm font-semibold text-slate-800 dark:text-slate-200" htmlFor="dashboard-feedback-title">
              {t("dashboard.report_title", { defaultValue: "Title" })}
            </label>
            <input
              id="dashboard-feedback-title"
              value={form.title}
              onChange={(event) => onFieldChange("title", event.target.value)}
              className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              placeholder={t("dashboard.report_title_ph", { defaultValue: "Short summary" })}
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-800 dark:text-slate-200" htmlFor="dashboard-feedback-body">
              {t("dashboard.report_body", { defaultValue: "What happened?" })}
            </label>
            <textarea
              id="dashboard-feedback-body"
              value={form.body}
              onChange={(event) => onFieldChange("body", event.target.value)}
              rows={4}
              className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              placeholder={t("dashboard.report_body_ph", { defaultValue: "Steps to reproduce, expected vs. actual" })}
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-800 dark:text-slate-200" htmlFor="dashboard-feedback-screenshots">
              {t("dashboard.report_screenshot", { defaultValue: "Screenshots (optional)" })}
            </label>
            <input
              id="dashboard-feedback-screenshots"
              type="file"
              multiple
              accept="image/*"
              className="mt-1 block w-full text-sm text-slate-700 dark:text-slate-200"
              onChange={(event) => onFilesChange(event.target.files)}
              disabled={loading}
            />
            {form.screenshots.length ? (
              <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300">
                {form.screenshots.map((screenshot, index) => (
                  <li key={`${screenshot.name}-${index}`} className="flex items-center justify-between rounded border border-slate-200 bg-slate-50 px-2 py-1 dark:border-slate-700 dark:bg-slate-800">
                    <span className="truncate">{screenshot.name || t("common.image", { defaultValue: "Image" })}</span>
                    <span className="text-slate-400">{Math.round(screenshot.size / 1024)} KB</span>
                  </li>
                ))}
                <li className="text-[11px] text-slate-500 dark:text-slate-400">
                  {t("dashboard.report_screenshot_added", {
                    defaultValue: "Allowed: images, max 5 MB each. You can add more files by selecting again.",
                  })}
                </li>
              </ul>
            ) : null}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t("dashboard.report_hint", {
              defaultValue: "We include the current page and your browser info to help debugging.",
            })}
          </p>
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:text-slate-900 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
            >
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={loading || !canSubmit}
              className="rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 disabled:opacity-50 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200"
            >
              {loading ? t("common.loading", { defaultValue: "Loading..." }) : t("common.send", { defaultValue: "Send" })}
            </button>
          </div>
        </>
      )}
    </div>
  </Modal>
);

export default DashboardFeedbackModal;
